import { organizationDetailsSlot } from '@/features/site-management/organizations/organization'
import { organizationFormSectionsSlot } from '@/features/site-management/organizations/paths'
import { detailsSlots } from '@/shared/details'
import type { Feature } from '@/shared/registry'
import { myDataSlot } from '@/shared/search/my-data'
import { BookOpen, CloudCheck, Settings } from '@/shared/ui/icons'

import { activityRoute } from './activities/activity'
import { courseCatalogNav, courseCatalogOf, courseCatalogPath, courseCatalogSlots, learnerCourseRoute } from './course-catalog/paths'
import { learnerLearningPathRoute, learnerPathCourseRoute, learningPathCatalogPath } from './learning-path-catalog/paths'
import {
  assessmentRoute,
  assessmentsPath,
  createAssessmentPath,
  designAssessmentRoute,
  editAssessmentRoute,
  previewAssessmentRoute,
  readAssessmentsPermission,
  writeAssessmentsPermission,
} from './assessments/paths'
import {
  courseCustomFieldRoute,
  courseCustomFieldsPath,
  createCourseCustomFieldPath,
  editCourseCustomFieldRoute,
  readCourseCustomFieldsPermission,
  writeCourseCustomFieldsPermission,
} from './custom-fields/paths'
import {
  courseDetailsSlot,
  courseFormSectionsSlot,
  courseOverviewSlot,
  courseResourcesSlot,
  courseRoute,
  coursesPath,
  createCoursePath,
  editCourseRoute,
  readCoursesPermission,
  writeCoursesPermission,
} from './courses/paths'
import { CatalogProvider } from './CatalogProvider'
import { courseFeedbackPath } from './feedback/course-feedback'
import { myDataApi } from './my-data/paths'
import { readLabProfilesPermission } from './labs/lab-profile'
import { labProfileRoute, labProfilesPath, labSeriesPath, labSeriesRoute } from './labs/paths'
import { createResourceRoute, editResourceRoute, manageResourcesRoute } from './files/course-file'
import {
  contentLibrariesPath,
  contentLibraryRoute,
  createContentLibraryPath,
  editContentLibraryRoute,
  readContentLibrariesPermission,
  writeContentLibrariesPermission,
} from './libraries/paths'
import {
  contentPackageRoute,
  contentPackagesPath,
  createContentPackagePath,
  editContentPackageRoute,
  readContentPackagesPermission,
  writeContentPackagesPermission,
} from './packages/paths'
import {
  createLearningPathPath,
  editLearningPathRoute,
  learningPathRoute,
  learningPathsPath,
  readLearningPathsPermission,
  writeLearningPathsPermission,
} from './learning-paths/paths'

import {
  createTagGroupPath,
  createTagPath,
  editTagGroupRoute,
  editTagRoute,
  organizationCatalogPath,
  organizationCatalogRoute,
  readTagsPermission,
  tagGroupRoute,
  tagGroupsPath,
  tagRoute,
  tagsPath,
  writeTagsPermission,
} from './tags/tag'

import {
  createStorageAccountPath,
  editStorageAccountRoute,
  readStorageAccountsPermission,
  storageAccountRoute,
  storageAccountsPath,
} from './storage-accounts/paths'
import {
  createVideoServicePath,
  editVideoServiceRoute,
  readVideoServicesPermission,
  videoServiceRoute,
  videoServicesPath,
} from './video-services/paths'
import {
  createPublishingGroupPath,
  editPublishingGroupRoute,
  publishingGroupRoute,
  publishingGroupsPath,
  readPublishingGroupsPermission,
  writePublishingGroupsPermission,
} from './publishing/paths'
import {
  createSurveyPath,
  designSurveyRoute,
  editSurveyRoute,
  readSurveysPermission,
  surveyRoute,
  surveysPath,
  writeSurveysPermission,
} from './surveys/paths'
import {
  contentProviderRoute,
  contentProvidersPath,
  createContentProviderPath,
  editContentProviderRoute,
  readContentProvidersPermission,
  writeContentProvidersPermission,
} from './content-providers/paths'
import {
  courseMaterialsRoute,
  createMaterialPath,
  createMaterialVendorPath,
  editMaterialRoute,
  editMaterialVendorRoute,
  materialRoute,
  materialsPath,
  materialVendorRoute,
  materialVendorsPath,
  readMaterialsPermission,
  writeMaterialsPermission,
} from './materials/paths'

/** Catalog: courses, activities, libraries, learning paths and the learner catalogs. Its screens register here. */
export const feature: Feature = {
  id: 'catalog',
  // Links a course's name only for a viewer who may open it, and shows Courses only while the catalog is on for them.
  provider: CatalogProvider,
  // The course catalog, for whoever browses it (CAT-29).
  nav: [courseCatalogNav],
  routes: [
    // The course catalog and a course's learner page (CAT-29). Signed out, each opens when the site and the course allow it.
    {
      path: courseCatalogPath,
      titleId: 'catalog.courseCatalog.eyebrow',
      page: () => import('./course-catalog/CourseCatalogPage').then((module) => module.CourseCatalogPage),
      public: true,
    },
    {
      path: learnerCourseRoute,
      titleId: 'catalog.learnerCourse.eyebrow',
      page: () => import('./course-catalog/LearnerCourseDetailsPage').then((module) => module.LearnerCourseDetailsPage),
      public: true,
    },
    // The learning path catalog and a path's learner page, signed in (CAT-30).
    {
      path: learningPathCatalogPath,
      titleId: 'catalog.learningPathCatalog.eyebrow',
      page: () => import('./learning-path-catalog/LearningPathCatalogPage').then((module) => module.LearningPathCatalogPage),
    },
    {
      path: learnerLearningPathRoute,
      titleId: 'catalog.learnerLearningPath.eyebrow',
      page: () => import('./learning-path-catalog/LearnerLearningPathPage').then((module) => module.LearnerLearningPathPage),
    },
    // A course the path still gives after the learner's organization lost the right to deliver it.
    {
      path: learnerPathCourseRoute,
      titleId: 'catalog.learnerCourse.eyebrow',
      page: () => import('./learning-path-catalog/LearnerPathCoursePage').then((module) => module.LearnerPathCoursePage),
    },
    // Find Courses (CAT-28).
    {
      path: coursesPath,
      titleId: 'catalog.search.title',
      page: () => import('./search/FindCoursesPage').then((module) => module.FindCoursesPage),
    },
    // Create, Edit and Course Details (CAT-02).
    {
      path: createCoursePath,
      titleId: 'catalog.courses.create.title',
      page: () => import('./courses/CourseFormPages').then((module) => module.CreateCoursePage),
    },
    {
      path: courseRoute,
      titleId: 'catalog.courses.details.title',
      page: () => import('./courses/CourseDetailsPage').then((module) => module.CourseDetailsPage),
    },
    {
      path: editCourseRoute,
      titleId: 'catalog.courses.edit.title',
      page: () => import('./courses/CourseFormPages').then((module) => module.EditCoursePage),
    },
    // Activity Details (CAT-03).
    {
      path: activityRoute,
      titleId: 'catalog.activities.details.title',
      page: () => import('./activities/ActivityDetailsPage').then((module) => module.ActivityDetailsPage),
    },
    // Manage Resources, Add and Edit Resource (CAT-13).
    {
      path: manageResourcesRoute,
      titleId: 'catalog.files.manage.title',
      page: () => import('./files/ManageResourcesPage').then((module) => module.ManageResourcesPage),
    },
    {
      path: createResourceRoute,
      titleId: 'catalog.files.create.title',
      page: () => import('./files/CourseFileFormPages').then((module) => module.CreateCourseFilePage),
    },
    {
      path: editResourceRoute,
      titleId: 'catalog.files.edit.title',
      page: () => import('./files/CourseFileFormPages').then((module) => module.EditCourseFilePage),
    },
    // Find Labs, Lab Details, Find Lab Series and Lab Series Details (CAT-33).
    {
      path: labProfilesPath,
      titleId: 'catalog.labs.find.title',
      page: () => import('./labs/FindLabProfilesPage').then((module) => module.FindLabProfilesPage),
    },
    {
      path: labProfileRoute,
      titleId: 'catalog.labs.details.title',
      page: () => import('./labs/LabProfileDetailsPage').then((module) => module.LabProfileDetailsPage),
    },
    {
      path: labSeriesPath,
      titleId: 'catalog.labs.series.find.title',
      page: () => import('./labs/FindLabSeriesPage').then((module) => module.FindLabSeriesPage),
    },
    {
      path: labSeriesRoute,
      titleId: 'catalog.labs.series.details.title',
      page: () => import('./labs/LabSeriesDetailsPage').then((module) => module.LabSeriesDetailsPage),
    },
    // Find, Details, Create and Edit Storage Account (CAT-12).
    {
      path: storageAccountsPath,
      titleId: 'catalog.storageAccounts.find.title',
      page: () => import('./storage-accounts/FindStorageAccountsPage').then((module) => module.FindStorageAccountsPage),
    },
    {
      path: createStorageAccountPath,
      titleId: 'catalog.storageAccounts.create.title',
      page: () => import('./storage-accounts/StorageAccountFormPages').then((module) => module.CreateStorageAccountPage),
    },
    {
      path: storageAccountRoute,
      titleId: 'catalog.storageAccounts.details.title',
      page: () => import('./storage-accounts/StorageAccountDetailsPage').then((module) => module.StorageAccountDetailsPage),
    },
    {
      path: editStorageAccountRoute,
      titleId: 'catalog.storageAccounts.edit.title',
      page: () => import('./storage-accounts/StorageAccountFormPages').then((module) => module.EditStorageAccountPage),
    },
    // Find, Details, Create and Edit External Video Provider (CAT-11).
    {
      path: videoServicesPath,
      titleId: 'catalog.videoServices.find.title',
      page: () => import('./video-services/FindVideoServicesPage').then((module) => module.FindVideoServicesPage),
    },
    {
      path: createVideoServicePath,
      titleId: 'catalog.videoServices.create.title',
      page: () => import('./video-services/VideoServiceFormPages').then((module) => module.CreateVideoServicePage),
    },
    {
      path: videoServiceRoute,
      titleId: 'catalog.videoServices.details.title',
      page: () => import('./video-services/VideoServiceDetailsPage').then((module) => module.VideoServiceDetailsPage),
    },
    {
      path: editVideoServiceRoute,
      titleId: 'catalog.videoServices.edit.title',
      page: () => import('./video-services/VideoServiceFormPages').then((module) => module.EditVideoServicePage),
    },
    // Find, Details, Create and Edit Content Provider (CAT-16).
    {
      path: contentProvidersPath,
      titleId: 'catalog.contentProviders.find.title',
      page: () => import('./content-providers/FindContentProvidersPage').then((module) => module.FindContentProvidersPage),
    },
    {
      path: createContentProviderPath,
      titleId: 'catalog.contentProviders.create.title',
      page: () => import('./content-providers/ContentProviderFormPages').then((module) => module.CreateContentProviderPage),
    },
    {
      path: contentProviderRoute,
      titleId: 'catalog.contentProviders.details.title',
      page: () => import('./content-providers/ContentProviderDetailsPage').then((module) => module.ContentProviderDetailsPage),
    },
    {
      path: editContentProviderRoute,
      titleId: 'catalog.contentProviders.edit.title',
      page: () => import('./content-providers/ContentProviderFormPages').then((module) => module.EditContentProviderPage),
    },
    // Find, Details, Create, Edit, Design and Preview Assessment (CAT-08).
    {
      path: assessmentsPath,
      titleId: 'catalog.assessments.find.title',
      page: () => import('./assessments/FindAssessmentsPage').then((module) => module.FindAssessmentsPage),
    },
    {
      path: createAssessmentPath,
      titleId: 'catalog.assessments.create.title',
      page: () => import('./assessments/AssessmentFormPages').then((module) => module.CreateAssessmentPage),
    },
    {
      path: assessmentRoute,
      titleId: 'catalog.assessments.details.title',
      page: () => import('./assessments/AssessmentDetailsPage').then((module) => module.AssessmentDetailsPage),
    },
    {
      path: editAssessmentRoute,
      titleId: 'catalog.assessments.edit.title',
      page: () => import('./assessments/AssessmentFormPages').then((module) => module.EditAssessmentPage),
    },
    {
      path: designAssessmentRoute,
      titleId: 'catalog.assessments.design.title',
      page: () => import('./assessments/AssessmentFormPages').then((module) => module.DesignAssessmentPage),
    },
    {
      path: previewAssessmentRoute,
      titleId: 'catalog.assessments.preview.title',
      page: () => import('./assessments/AssessmentPreviewPage').then((module) => module.AssessmentPreviewPage),
    },
    // Find, Details, Create and Edit Content Library (CAT-25).
    {
      path: contentLibrariesPath,
      titleId: 'catalog.libraries.find.title',
      page: () => import('./libraries/FindContentLibrariesPage').then((module) => module.FindContentLibrariesPage),
    },
    {
      path: createContentLibraryPath,
      titleId: 'catalog.libraries.create.title',
      page: () => import('./libraries/ContentLibraryFormPages').then((module) => module.CreateContentLibraryPage),
    },
    {
      path: contentLibraryRoute,
      titleId: 'catalog.libraries.details.title',
      page: () => import('./libraries/ContentLibraryDetailsPage').then((module) => module.ContentLibraryDetailsPage),
    },
    {
      path: editContentLibraryRoute,
      titleId: 'catalog.libraries.edit.title',
      page: () => import('./libraries/ContentLibraryFormPages').then((module) => module.EditContentLibraryPage),
    },
    // Find, Details, Create and Edit SCORM Module (CAT-06).
    {
      path: contentPackagesPath,
      titleId: 'catalog.packages.find.title',
      page: () => import('./packages/FindContentPackagesPage').then((module) => module.FindContentPackagesPage),
    },
    {
      path: createContentPackagePath,
      titleId: 'catalog.packages.create.title',
      page: () => import('./packages/ContentPackageFormPages').then((module) => module.CreateContentPackagePage),
    },
    {
      path: contentPackageRoute,
      titleId: 'catalog.packages.details.title',
      page: () => import('./packages/ContentPackageDetailsPage').then((module) => module.ContentPackageDetailsPage),
    },
    {
      path: editContentPackageRoute,
      titleId: 'catalog.packages.edit.title',
      page: () => import('./packages/ContentPackageFormPages').then((module) => module.EditContentPackagePage),
    },
    // Find, Details, Create and Edit Tag and Tag Group, and an organization's catalog settings (CAT-15).
    { path: tagsPath, titleId: 'catalog.tags.find.title', page: () => import('./tags/FindTagsPages').then((module) => module.FindTagsPage) },
    { path: createTagPath, titleId: 'catalog.tags.create.title', page: () => import('./tags/TagForms').then((module) => module.CreateTagPage) },
    { path: tagRoute, titleId: 'catalog.tags.details.title', page: () => import('./tags/TagDetailsPages').then((module) => module.TagDetailsPage) },
    { path: editTagRoute, titleId: 'catalog.tags.edit.title', page: () => import('./tags/TagForms').then((module) => module.EditTagPage) },
    {
      path: tagGroupsPath,
      titleId: 'catalog.tagGroups.find.title',
      page: () => import('./tags/FindTagsPages').then((module) => module.FindTagGroupsPage),
    },
    {
      path: createTagGroupPath,
      titleId: 'catalog.tagGroups.create.title',
      page: () => import('./tags/TagForms').then((module) => module.CreateTagGroupPage),
    },
    {
      path: tagGroupRoute,
      titleId: 'catalog.tagGroups.details.title',
      page: () => import('./tags/TagDetailsPages').then((module) => module.TagGroupDetailsPage),
    },
    {
      path: editTagGroupRoute,
      titleId: 'catalog.tagGroups.edit.title',
      page: () => import('./tags/TagForms').then((module) => module.EditTagGroupPage),
    },
    {
      path: organizationCatalogRoute,
      titleId: 'catalog.organizationCatalog.title',
      page: () => import('./tags/OrganizationCatalogPage').then((module) => module.OrganizationCatalogPage),
    },
    // Find, Details, Create and Edit Publishing Group (CAT-27).
    {
      path: publishingGroupsPath,
      titleId: 'catalog.publishing.find.title',
      page: () => import('./publishing/FindPublishingGroupsPage').then((module) => module.FindPublishingGroupsPage),
    },
    {
      path: createPublishingGroupPath,
      titleId: 'catalog.publishing.create.title',
      page: () => import('./publishing/PublishingGroupFormPages').then((module) => module.CreatePublishingGroupPage),
    },
    {
      path: publishingGroupRoute,
      titleId: 'catalog.publishing.details.title',
      page: () => import('./publishing/PublishingGroupDetailsPage').then((module) => module.PublishingGroupDetailsPage),
    },
    {
      path: editPublishingGroupRoute,
      titleId: 'catalog.publishing.edit.title',
      page: () => import('./publishing/PublishingGroupFormPages').then((module) => module.EditPublishingGroupPage),
    },
    // Find, Details, Create and Edit Learning Path (CAT-26).
    {
      path: learningPathsPath,
      titleId: 'catalog.learningPaths.find.title',
      page: () => import('./learning-paths/FindLearningPathsPage').then((module) => module.FindLearningPathsPage),
    },
    {
      path: createLearningPathPath,
      titleId: 'catalog.learningPaths.create.title',
      page: () => import('./learning-paths/LearningPathFormPages').then((module) => module.CreateLearningPathPage),
    },
    {
      path: learningPathRoute,
      titleId: 'catalog.learningPaths.details.title',
      page: () => import('./learning-paths/LearningPathDetailsPage').then((module) => module.LearningPathDetailsPage),
    },
    {
      path: editLearningPathRoute,
      titleId: 'catalog.learningPaths.edit.title',
      page: () => import('./learning-paths/LearningPathFormPages').then((module) => module.EditLearningPathPage),
    },
    // Find, Details, Create and Edit Material Vendor and Material, and a course's Materials (CAT-20).
    {
      path: materialVendorsPath,
      titleId: 'catalog.materials.vendors.find.title',
      page: () => import('./materials/FindMaterialVendorsPage').then((module) => module.FindMaterialVendorsPage),
    },
    {
      path: createMaterialVendorPath,
      titleId: 'catalog.materials.vendors.create.title',
      page: () => import('./materials/MaterialFormPages').then((module) => module.CreateMaterialVendorPage),
    },
    {
      path: materialVendorRoute,
      titleId: 'catalog.materials.vendors.details.title',
      page: () => import('./materials/MaterialVendorDetailsPage').then((module) => module.MaterialVendorDetailsPage),
    },
    {
      path: editMaterialVendorRoute,
      titleId: 'catalog.materials.vendors.edit.title',
      page: () => import('./materials/MaterialFormPages').then((module) => module.EditMaterialVendorPage),
    },
    {
      path: materialsPath,
      titleId: 'catalog.materials.find.title',
      page: () => import('./materials/FindMaterialsPage').then((module) => module.FindMaterialsPage),
    },
    {
      path: createMaterialPath,
      titleId: 'catalog.materials.create.title',
      page: () => import('./materials/MaterialFormPages').then((module) => module.CreateMaterialPage),
    },
    {
      path: materialRoute,
      titleId: 'catalog.materials.details.title',
      page: () => import('./materials/MaterialDetailsPage').then((module) => module.MaterialDetailsPage),
    },
    {
      path: editMaterialRoute,
      titleId: 'catalog.materials.edit.title',
      page: () => import('./materials/MaterialFormPages').then((module) => module.EditMaterialPage),
    },
    {
      path: courseMaterialsRoute,
      titleId: 'catalog.materials.lists.title',
      page: () => import('./materials/CourseMaterialsPage').then((module) => module.CourseMaterialsPage),
    },
    // Find, Details, Create, Edit and Design Survey (CAT-09).
    {
      path: surveysPath,
      titleId: 'catalog.surveys.find.title',
      page: () => import('./surveys/FindSurveysPage').then((module) => module.FindSurveysPage),
    },
    {
      path: createSurveyPath,
      titleId: 'catalog.surveys.create.title',
      page: () => import('./surveys/SurveyFormPages').then((module) => module.CreateSurveyPage),
    },
    {
      path: surveyRoute,
      titleId: 'catalog.surveys.eyebrow',
      page: () => import('./surveys/SurveyDetailsPage').then((module) => module.SurveyDetailsPage),
    },
    {
      path: editSurveyRoute,
      titleId: 'catalog.surveys.edit.title',
      page: () => import('./surveys/SurveyFormPages').then((module) => module.EditSurveyPage),
    },
    {
      path: designSurveyRoute,
      titleId: 'catalog.surveys.design.title',
      page: () => import('./surveys/SurveyFormPages').then((module) => module.DesignSurveyPage),
    },
    // Find Course Feedback (CAT-21).
    {
      path: courseFeedbackPath,
      titleId: 'catalog.feedback.find.title',
      page: () => import('./feedback/FindCourseFeedbackPage').then((module) => module.FindCourseFeedbackPage),
    },
    // Find, Details, Create and Edit Course Custom Field (CAT-23).
    {
      path: courseCustomFieldsPath,
      titleId: 'catalog.customFields.find.title',
      page: () => import('./custom-fields/FindCourseCustomFieldsPage').then((module) => module.FindCourseCustomFieldsPage),
    },
    {
      path: createCourseCustomFieldPath,
      titleId: 'catalog.customFields.create.title',
      page: () => import('./custom-fields/CourseCustomFieldFormPages').then((module) => module.CreateCourseCustomFieldPage),
    },
    {
      path: courseCustomFieldRoute,
      titleId: 'catalog.customFields.details.title',
      page: () => import('./custom-fields/CourseCustomFieldDetailsPage').then((module) => module.CourseCustomFieldDetailsPage),
    },
    {
      path: editCourseCustomFieldRoute,
      titleId: 'catalog.customFields.edit.title',
      page: () => import('./custom-fields/CourseCustomFieldFormPages').then((module) => module.EditCourseCustomFieldPage),
    },
  ],
  adminHome: {
    categories: [{ id: 'storage-accounts', section: 'system', titleId: 'catalog.storageAccounts.adminHomeCategory', icon: CloudCheck }],
    // Create needs write and the credentials aspect, and a link names one permission, so Create is on Find Storage Accounts.
    links: [
      { id: 'find-courses', category: 'courses', labelId: 'catalog.search.title', path: coursesPath, permission: readCoursesPermission },
      { id: 'create-course', category: 'courses', labelId: 'catalog.courses.create.title', path: createCoursePath, permission: writeCoursesPermission },
      { id: 'find-labs', category: 'labs', labelId: 'catalog.labs.find.title', path: labProfilesPath, permission: readLabProfilesPermission },
      { id: 'find-lab-series', category: 'labs', labelId: 'catalog.labs.series.find.title', path: labSeriesPath, permission: readLabProfilesPermission },
      // Content libraries are new in v2, so the mockups' Admin Home has no place for them: they sit with courses (CAT-25).
      {
        id: 'find-content-libraries',
        category: 'courses',
        labelId: 'catalog.libraries.find.title',
        path: contentLibrariesPath,
        permission: readContentLibrariesPermission,
      },
      {
        id: 'create-content-library',
        category: 'courses',
        labelId: 'catalog.libraries.create.title',
        path: createContentLibraryPath,
        permission: writeContentLibrariesPermission,
      },
      // The mockups' Admin Home has no place for publishing groups: they sit with courses (CAT-27).
      {
        id: 'find-publishing-groups',
        category: 'courses',
        labelId: 'catalog.publishing.find.title',
        path: publishingGroupsPath,
        permission: readPublishingGroupsPermission,
      },
      {
        id: 'create-publishing-group',
        category: 'courses',
        labelId: 'catalog.publishing.create.title',
        path: createPublishingGroupPath,
        permission: writePublishingGroupsPermission,
      },
      // The mockups' Admin Home has a SCORM card (CAT-06).
      {
        id: 'find-scorm-modules',
        category: 'scorm',
        labelId: 'catalog.packages.find.title',
        path: contentPackagesPath,
        permission: readContentPackagesPermission,
      },
      {
        id: 'create-scorm-module',
        category: 'scorm',
        labelId: 'catalog.packages.create.title',
        path: createContentPackagePath,
        permission: writeContentPackagesPermission,
      },
      // The mockups' Admin Home has no place for tags: they sit with courses (CAT-15).
      { id: 'find-tags', category: 'courses', labelId: 'catalog.tags.find.title', path: tagsPath, permission: readTagsPermission },
      { id: 'create-tag', category: 'courses', labelId: 'catalog.tags.create.title', path: createTagPath, permission: writeTagsPermission },
      { id: 'find-tag-groups', category: 'courses', labelId: 'catalog.tagGroups.find.title', path: tagGroupsPath, permission: readTagsPermission },
      {
        id: 'create-tag-group',
        category: 'courses',
        labelId: 'catalog.tagGroups.create.title',
        path: createTagGroupPath,
        permission: writeTagsPermission,
      },
      // The mockups' Admin Home lists Learning Paths under Subscriptions (CAT-26).
      {
        id: 'find-learning-paths',
        category: 'subscriptions',
        labelId: 'catalog.learningPaths.find.title',
        path: learningPathsPath,
        permission: readLearningPathsPermission,
      },
      {
        id: 'create-learning-path',
        category: 'subscriptions',
        labelId: 'catalog.learningPaths.create.title',
        path: createLearningPathPath,
        permission: writeLearningPathsPermission,
      },
      { id: 'find-assessments', category: 'assessments', labelId: 'catalog.assessments.find.title', path: assessmentsPath, permission: readAssessmentsPermission },
      { id: 'create-assessment', category: 'assessments', labelId: 'catalog.assessments.create.title', path: createAssessmentPath, permission: writeAssessmentsPermission },
      {
        id: 'find-storage-accounts',
        category: 'storage-accounts',
        labelId: 'catalog.storageAccounts.find.title',
        path: storageAccountsPath,
        permission: readStorageAccountsPermission,
      },
      {
        id: 'find-content-providers',
        category: 'content-providers',
        labelId: 'catalog.contentProviders.find.title',
        path: contentProvidersPath,
        permission: readContentProvidersPermission,
      },
      {
        id: 'create-content-provider',
        category: 'content-providers',
        labelId: 'catalog.contentProviders.create.title',
        path: createContentProviderPath,
        permission: writeContentProvidersPermission,
      },
      // The mockups' Admin Home lists external video providers under Content Providers; Create is on
      // Find External Video Providers, as it needs write and the credentials aspect (CAT-11).
      {
        id: 'find-video-services',
        category: 'content-providers',
        labelId: 'catalog.videoServices.find.title',
        path: videoServicesPath,
        permission: readVideoServicesPermission,
      },
      { id: 'find-materials', category: 'materials', labelId: 'catalog.materials.find.title', path: materialsPath, permission: readMaterialsPermission },
      { id: 'create-material', category: 'materials', labelId: 'catalog.materials.create.title', path: createMaterialPath, permission: writeMaterialsPermission },
      {
        id: 'find-material-vendors',
        category: 'material-vendors',
        labelId: 'catalog.materials.vendors.find.title',
        path: materialVendorsPath,
        permission: readMaterialsPermission,
      },
      {
        id: 'create-material-vendor',
        category: 'material-vendors',
        labelId: 'catalog.materials.vendors.create.title',
        path: createMaterialVendorPath,
        permission: writeMaterialsPermission,
      },
      { id: 'find-surveys', category: 'surveys', labelId: 'catalog.surveys.find.title', path: surveysPath, permission: readSurveysPermission },
      { id: 'create-survey', category: 'surveys', labelId: 'catalog.surveys.create.title', path: createSurveyPath, permission: writeSurveysPermission },
      // The mockups' Admin Home lists Course Custom Fields under Courses (CAT-23).
      {
        id: 'find-course-custom-fields',
        category: 'courses',
        labelId: 'catalog.customFields.find.title',
        path: courseCustomFieldsPath,
        permission: readCourseCustomFieldsPermission,
      },
      {
        id: 'create-course-custom-field',
        category: 'courses',
        labelId: 'catalog.customFields.create.title',
        path: createCourseCustomFieldPath,
        permission: writeCourseCustomFieldsPermission,
      },
    ],
  },
  slots: [
    // Catalog's part of "Download my data" on My Profile (CAT-35).
    { kind: 'link', slot: myDataSlot, id: 'catalog.my-data', labelId: 'catalog.myData.part', path: myDataApi },
    {
      // An organization's catalog settings page: its tag groups (CAT-15).
      kind: 'link',
      slot: detailsSlots(organizationDetailsSlot).menu,
      id: 'catalog-settings',
      labelId: 'catalog.organizationCatalog.title',
      icon: Settings,
      path: (context) => organizationCatalogPath(context.recordId ?? ''),
    },
    {
      // The Prerequisites table in Course Details' Overview (CAT-17).
      kind: 'panel',
      slot: courseOverviewSlot,
      id: 'prerequisites',
      component: () => import('./courses/prerequisites/PrerequisitesPanel').then((module) => module.PrerequisitesPanel),
    },
    {
      // Course Resources on Course Details' Resources tab (CAT-13).
      kind: 'panel',
      slot: courseResourcesSlot,
      id: 'course-files',
      component: () => import('./files/CourseFilesPanel').then((module) => module.CourseFilesPanel),
    },
    {
      // Meet The Presenter in Course Details' Resources, after the course's files (CAT-18).
      kind: 'panel',
      slot: courseResourcesSlot,
      id: 'presenters',
      component: () => import('./courses/presenters/PresentersPanel').then((module) => module.PresentersPanel),
    },
    {
      // "Help us improve" in Course Details' Resources, after Meet The Presenter (CAT-21).
      kind: 'panel',
      slot: courseResourcesSlot,
      id: 'feedback',
      component: () => import('./feedback/CourseFeedbackPanel').then((module) => module.CourseFeedbackPanel),
    },
    {
      // "Add to Library", on Course Details (CAT-25).
      kind: 'action',
      slot: detailsSlots(courseDetailsSlot).actions,
      id: 'add-to-library',
      component: () => import('./libraries/AddToLibraryAction').then((module) => module.AddToLibraryAction),
    },
    {
      // "Materials", on Course Details: the course's materials lists (CAT-20).
      kind: 'action',
      slot: detailsSlots(courseDetailsSlot).actions,
      id: 'materials',
      component: () => import('./materials/MaterialsAction').then((module) => module.MaterialsAction),
    },
    {
      // The `catalog` settings group on Create and Edit Organization (CAT-34).
      kind: 'tab',
      slot: organizationFormSectionsSlot,
      id: 'catalog',
      labelId: 'catalog.settings.label',
      component: () => import('./settings/CatalogSettingsSection').then((module) => module.CatalogSettingsSection),
    },
    {
      // The course's custom fields, by display group, on Create and Edit Course (CAT-23).
      kind: 'tab',
      slot: courseFormSectionsSlot,
      id: 'custom-fields',
      labelId: 'catalog.customFields.courseSection.label',
      component: () => import('./custom-fields/CourseCustomFieldsSection').then((module) => module.CourseCustomFieldsSection),
    },
    {
      // The course's custom fields in Course Details' Overview (CAT-23).
      kind: 'panel',
      slot: courseOverviewSlot,
      id: 'custom-fields',
      component: () => import('./custom-fields/CourseCustomFieldsPanel').then((module) => module.CourseCustomFieldsPanel),
    },
    {
      // "Course Catalog" on Organization Details' side menu: the organization's catalog (CAT-29).
      kind: 'link',
      slot: detailsSlots(organizationDetailsSlot).menu,
      id: 'course-catalog',
      labelId: 'catalog.courseCatalog.eyebrow',
      icon: BookOpen,
      path: (context) => courseCatalogOf(context.recordId),
    },
    {
      // "Help us improve" in Resources on a course's learner page, after the presenters (CAT-29).
      kind: 'panel',
      slot: courseCatalogSlots.resourcesEnd,
      id: 'feedback',
      component: () => import('./feedback/CourseFeedbackPanel').then((module) => module.CourseFeedbackPanel),
    },
  ],
}
